// ════════════════════════════════════════════════════════════════════════════
// src/ui/CallLinkModal.jsx — a link that rings your phone, and the record of
// every one you have made.
//
// The sibling of GuestLinkModal, and deliberately NOT the same modal with a
// switch in it, because the two links behave differently in the ways that
// matter on screen:
//
//   🟥 A CALL LINK IS SPENT BY A CONVERSATION, NOT BY THE CLOCK. A chat link
//      lives its full 1/3/7/15 days; a call link dies the moment somebody
//      actually reaches you. So "3 days left" is not the headline here — "has it
//      been used" is, and a spent link has to look finished even though its
//      expiry is still days away.
//
//   🟥 AND THERE IS NO CONVERSATION TO END. Revoke on a chat link hangs up on a
//      thread and deletes it. Here there is nothing to delete but the link
//      itself, so "End now" means only "stop this from working".
//
// 🟢 EVERY ATTEMPT IS SHOWN, NOT JUST THE ONE THAT WORKED. The server records
//    each try (see noteGuestCallEnded) and only an ANSWERED call spends the
//    link. Without that line, a link that rang four times unanswered and one
//    nobody ever opened read identically — and they are the difference between
//    "they could not reach me" and "they never tried".
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from 'react';
import {
  LINK_DAYS, createGuestLink, revokeGuestLink, deleteGuestLink,
  watchMyGuestLinks, callLinkState, callAttemptSummary, timeLeftLabel,
} from '../services/guestLinks';
import {
  X, Phone, Lock, Timer, Check, Trash, Person, PhoneOff, Video, Link as LinkIcon,
} from './icons';

const DAY_LABEL = { 1: '1 day', 3: '3 days', 7: '1 week', 15: '15 days' };

/* 🟢 FIVE STATES, FIVE SENTENCES. `callLinkState` already collapses the row into
   one word; this is the only place that word becomes something a person reads,
   so the phrasing lives here rather than in the service — the phone says the
   same five things in ten languages and cannot share an English string. */
const STATE = {
  waiting: { label: 'Not opened yet', cls: 'wait' },
  ready: { label: 'Opened — not called yet', cls: 'wait' },
  used: { label: 'Used', cls: 'done' },
  expired: { label: 'Expired', cls: 'dead' },
  revoked: { label: 'Ended by you', cls: 'dead' },
};

const shortDate = (ms) => (ms ? new Date(ms).toLocaleDateString() : '');

/** "4m 12s" — the seconds actually spoken. */
function talkLabel(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  if (!s) return '';
  const m = Math.floor(s / 60);
  return m ? `${m}m ${s % 60}s` : `${s}s`;
}

/** The one line under a row that says what actually happened on this link. */
function attemptLine(link) {
  const a = callAttemptSummary(link);
  if (!a.total) return '';
  if (a.answered) {
    const spoke = talkLabel(a.talkedSec);
    return spoke ? `answered · talked for ${spoke}` : 'answered';
  }
  return a.total === 1 ? 'rang once, not answered' : `rang ${a.total} times, not answered`;
}

export default function CallLinkModal({ myUid, onClose }) {
  const [tab, setTab] = useState('new');
  const [days, setDays] = useState(1);
  const [wantPasscode, setWantPasscode] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState(null);
  const [copied, setCopied] = useState('');
  const [err, setErr] = useState('');
  const [links, setLinks] = useState([]);
  const [confirm, setConfirm] = useState(null);   // { token, name, mode: 'end'|'delete' }

  /* 🟥 CALL LINKS ONLY. Chat links share the collection; listed here they would
     offer a Copy button for a URL that opens a conversation, from the Calls
     column. */
  useEffect(() => watchMyGuestLinks(myUid, setLinks, 'call'), [myUid]);

  const live = useMemo(() => links.filter((l) => l.live), [links]);
  const past = useMemo(() => links.filter((l) => !l.live), [links]);

  const create = async () => {
    setBusy(true); setErr('');
    try {
      const r = await createGuestLink({
        kind: 'call',
        days,
        passcode: wantPasscode ? passcode : '',
        note: note.trim(),
      });
      setMade(r);
      setPasscode(''); setNote('');
    } catch (e) {
      /* 🟢 SAY WHICH FAILURE. "Try again" is right for a flaky network and wrong
         for every other case here: too_many_links means end one first, and an
         http_404 means the route is not deployed — which is a thing the person
         reading this can actually act on. */
      setErr(
        e.code === 'too_many_links'
          ? 'You have a lot of open links already — end one first.'
          : e.code === 'http_404'
            ? 'This server does not have call links yet. Deploy the server and try again.'
            : 'Could not create the link. Try again.',
      );
    }
    setBusy(false);
  };

  const copy = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(id);
      setTimeout(() => setCopied((c) => (c === id ? '' : c)), 1800);
    } catch { /* the field is selectable — nothing is lost */ }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.mode === 'end') await revokeGuestLink(confirm.token);
      else await deleteGuestLink(confirm.token);
    } catch {
      setErr('That did not work. Try again.');
    }
    setBusy(false);
    setConfirm(null);
  };

  const rowOf = (l) => {
    const st = callLinkState(l);
    const s = STATE[st] || STATE.expired;
    const line = attemptLine(l);
    return (
      <div className={`gl-row cl-row${l.live ? '' : ' done'}`} key={l.id}>
        <span className="gl-row-ava">
          {l.guest?.image ? <img src={l.guest.image} alt="" /> : <Person width={16} height={16} />}
        </span>
        <span className="gl-row-id">
          <b>{l.guest?.name || l.note || 'Nobody has opened this yet'}</b>
          <i>
            {l.guest?.email ? `${l.guest.email} · ` : ''}
            {l.live ? timeLeftLabel(l.expiresAt) : (shortDate(l.createdAt) || 'ended')}
            {l.passcodeHash ? ' · passcode' : ''}
            {line ? ` · ${line}` : ''}
          </i>
        </span>
        <span className={`cl-pill ${s.cls}`}>{s.label}</span>
        <span className="cl-acts">
          {l.live && l.url && (
            <button
              className="gl-end cl-copy"
              title="Copy the link"
              onClick={() => copy(l.url, l.id)}
            >
              {copied === l.id ? <Check width={15} height={15} /> : <LinkIcon width={15} height={15} />}
            </button>
          )}
          {l.live && (
            <button
              className="gl-end"
              title="Stop this link working"
              onClick={() => setConfirm({ token: l.token || l.id, name: l.guest?.name || l.note || '', mode: 'end' })}
            >
              <PhoneOff width={15} height={15} />
            </button>
          )}
          {/* 🟥 DELETE IS OFFERED ON EVERY ROW, LIVE ONES INCLUDED, and it is a
              different button from End. The server revokes first when the link
              is still live, so removing a row can never leave a working token
              pointing at a record nobody can see. */}
          <button
            className="gl-end"
            title="Remove from this list"
            onClick={() => setConfirm({ token: l.token || l.id, name: l.guest?.name || l.note || '', mode: 'delete' })}
          >
            <Trash width={15} height={15} />
          </button>
        </span>
      </div>
    );
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="gl-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gl-head">
          <b><Phone width={17} height={17} /> Call someone not on Allo</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="gl-tabs">
          <button className={tab === 'new' ? 'on' : ''} onClick={() => setTab('new')}>New link</button>
          <button className={tab === 'list' ? 'on' : ''} onClick={() => setTab('list')}>
            My call links{links.length ? <i>{links.length}</i> : null}
          </button>
        </div>

        {tab === 'new' ? (
          <div className="gl-body">
            {!made ? (
              <>
                <p className="gl-lede">
                  They open the link in any browser, give a name and an email, pick voice or
                  video — and your phone rings like any other call. No app, no account.
                </p>

                <div className="gl-label">The link works for</div>
                <div className="gl-days">
                  {LINK_DAYS.map((d) => (
                    <button key={d} className={days === d ? 'on' : ''} onClick={() => setDays(d)}>
                      {DAY_LABEL[d]}
                    </button>
                  ))}
                </div>

                <label className="gl-toggle">
                  <input type="checkbox" checked={wantPasscode} onChange={(e) => setWantPasscode(e.target.checked)} />
                  <span><Lock width={13} height={13} /> Require a passcode</span>
                </label>
                {wantPasscode && (
                  <input
                    className="gl-input" value={passcode} maxLength={24}
                    onChange={(e) => setPasscode(e.target.value)}
                    placeholder="Passcode you will tell them separately"
                  />
                )}

                <input
                  className="gl-input" value={note} maxLength={40}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Label, so you know whose link this is (optional)"
                />

                {err && <div className="gl-err">{err}</div>}

                <button className="gl-go" onClick={create} disabled={busy || (wantPasscode && !passcode)}>
                  {busy ? 'Creating…' : 'Create the call link'}
                </button>
                {/* 🟥 SAY THE ONE-CALL RULE BEFORE IT IS MADE, not after. Somebody
                    who sends the same link twice and finds it dead the second
                    time reads that as a bug in Allo. */}
                <p className="gl-fine">
                  One link, one call. It stops working as soon as you have spoken — even if
                  there are days left on it.
                </p>
              </>
            ) : (
              <div className="gl-made">
                <div className="gl-made-h"><Check width={16} height={16} /> Call link ready</div>
                <div className="gl-url">
                  <input readOnly value={made.url} onFocus={(e) => e.target.select()} />
                  <button onClick={() => copy(made.url, 'made')}>{copied === 'made' ? 'Copied' : 'Copy'}</button>
                </div>
                <div className="gl-made-meta">
                  <span><Timer width={13} height={13} /> {DAY_LABEL[made.durationDays] || 'link'}</span>
                  <span><Phone width={13} height={13} /> Voice</span>
                  <span><Video width={13} height={13} /> or video</span>
                  {made.needsPasscode && <span><Lock width={13} height={13} /> Passcode required</span>}
                </div>
                <p className="gl-fine">
                  Send it however you like. Tell them the passcode a different way — a link and
                  its passcode in the same message protect nothing.
                </p>
                <button className="gl-again" onClick={() => setMade(null)}>Create another</button>
              </div>
            )}
          </div>
        ) : (
          <div className="gl-body gl-list">
            {err && <div className="gl-err">{err}</div>}
            {links.length === 0 && (
              <div className="gl-empty">
                No call links yet. Make one and send it to somebody who is not on Allo.
              </div>
            )}

            {live.length > 0 && <div className="gl-label">Still working</div>}
            {live.map(rowOf)}

            {/* 🟢 THE ENDED HALF IS THE RECORD, and it is the reason the list is
                worth opening at all: the link is gone, who called and whether
                they got through is not. */}
            {past.length > 0 && <div className="gl-label">Finished</div>}
            {past.map(rowOf)}
          </div>
        )}

        {confirm && (
          <div className="cl-confirm">
            <b>
              {confirm.mode === 'end'
                ? 'Stop this link working?'
                : 'Remove this from your list?'}
            </b>
            <p>
              {confirm.mode === 'end'
                ? 'Anyone holding it gets "this link has expired". Nothing else changes, and the row stays in your list.'
                : 'The row disappears and the link stops working if it still was. This cannot be undone.'}
            </p>
            <div className="cl-confirm-foot">
              <button className="gl-again" disabled={busy} onClick={() => setConfirm(null)}>Cancel</button>
              <button className="gl-go cl-danger" disabled={busy} onClick={runConfirm}>
                {busy ? 'Working…' : confirm.mode === 'end' ? 'Stop it' : 'Remove'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
